import { Dispatch, SetStateAction } from "react";
import { Typography } from "@foodpilot/foods";
import { Box, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import { BrandField } from "src/pages/Product/ProductForm/Pages/Page1/Fields/BrandField";
import { OwnedByField } from "src/pages/Product/ProductForm/Pages/Page1/Fields/OwnedByField";
import { MODIFY, PRODUCT_SETTINGS_ORGANIZATION, useFoodpilotContext } from "src/context/FoodpilotContext";
import { useGetAllTagTypes } from "src/api/client-api/tag/useGetAllTagTypes";
import { useOrganizationSectionVisibility } from "src/pages/Product/hooks/useOrganizationSectionVisibility";
import { useGetAllBrands } from "src/api/client-api/brand/useGetAllBrand";

import { AssociatedStructuresFieldV2 } from "./AssociatedStructuresFieldV2";
import { ProductRangeFieldV2 } from "./ProductRangeFieldV2";
import { ProductTagFieldV2 } from "./ProductTagFieldV2";

const brandSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
});

const productLineSchema = z.object({
    "@id": z.string(),
    name: z.string(),
});

const organizationSchema = z.object({
    brand: brandSchema.optional(),
    productLine: productLineSchema,
    ownedBy: z.object({
        "@id": z.string(),
        id: z.number(),
        name: z.string(),
    }),
});

type OrganizationFormData = z.infer<typeof organizationSchema>;

type Step3OrganizationProps = {
    product: ProductFormType;
    setProduct: Dispatch<SetStateAction<ProductFormType>>;
    companyIds: { "@id": string; id: number; name: string };
};

/**
 * Step 3 - Organization (Figma empty `12819:67442`, filled `13154:221653`).
 *
 * Layout:
 *  - Top row: Range (productLine) + Brand in a 2-column grid
 *  - Optional Owned-by selector when the active company is not the owner
 *  - Associated entities (gated to owners + ORGANIZATION permission)
 *  - Tags subsection: 2-column grid of tag-type dropdowns (`ProductTagFieldV2`)
 *
 * Returns bare section contents — `SectionCard` + `SectionHeader` are provided by
 * `ProductFormV2`.
 */
export const Step3Organization = (props: Step3OrganizationProps) => {
    const { product, setProduct, companyIds } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const { canUser } = useFoodpilotContext();

    const { control } = useForm<OrganizationFormData>({
        resolver: zodResolver(organizationSchema),
        mode: "onChange",
        reValidateMode: "onBlur",
        defaultValues: {
            brand: product.brand ?? undefined,
            productLine: product.productLine ?? undefined,
            ownedBy: product.ownedBy ?? companyIds,
        },
    });

    const owningCompany = product.ownedBy ?? companyIds;
    const isOwner = companyIds.id === owningCompany.id;
    const canShowAssociatedStructure = isOwner && canUser(MODIFY, PRODUCT_SETTINGS_ORGANIZATION);

    // Hide the "Tags" subsection when no tag type is defined for the product context.
    // Params mirror those used inside ProductTagFieldV2 so React Query deduplicates the request.
    const { tagTypes = [] } = useGetAllTagTypes({ context: "product", itemsPerPage: 100, active: true });
    const hasProductTagTypes = tagTypes.length > 0;

    // Hide the "Brand" field when the active organization has no brand.
    // Stable existence-only request, independent of the search-driven query
    // inside BrandField.
    const { brandCollectionState } = useGetAllBrands({ itemsPerPage: 1 });
    const hasBrands = brandCollectionState.status !== "empty";

    // Hide the "Range" (productLine) field when the active org has no product
    // line yet. validateProductForm already accepts hasProductLines=false.
    const { hasProductLines } = useOrganizationSectionVisibility({ currentCompanyId: companyIds.id });

    // Two columns only when BOTH fields render; collapse to one otherwise so
    // the surviving field doesn't sit next to an empty cell.
    const hasOrganizationFields = hasBrands || hasProductLines;
    const showTwoColumns = hasBrands && hasProductLines;
    const hasContentBeforeTags = hasOrganizationFields || !isOwner || canShowAssociatedStructure;

    return (
        <Stack sx={{ gap: theme.spacing(3), padding: theme.spacing(3), paddingBottom: theme.spacing(2) }}>
            {hasOrganizationFields && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: showTwoColumns ? { xs: "1fr", md: "1fr 1fr" } : "1fr",
                        columnGap: theme.spacing(2),
                        rowGap: theme.spacing(3),
                    }}
                >
                    {hasProductLines && (
                        <Controller
                            name="productLine"
                            control={control}
                            render={({ field }) => (
                                <ProductRangeFieldV2
                                    productLineIRI={product.productLine?.["@id"] ?? null}
                                    label={t("product.v2.organization.range", "Range")}
                                    placeholder={t("product.v2.organization.select_range", "Select a range")}
                                    onChange={(value) => {
                                        field.onChange(value);
                                        setProduct((prevState) => ({ ...prevState, productLine: value }));
                                    }}
                                />
                            )}
                        />
                    )}
                    {hasBrands && (
                        <Controller
                            name="brand"
                            control={control}
                            render={({ field }) => (
                                <BrandField
                                    brandIRI={product.brand?.["@id"] || null}
                                    appearance="product-v2"
                                    label={t("marque", "Brand")}
                                    placeholder={t("product.v2.organization.select_brand", "Select a brand")}
                                    setBrand={(value) => {
                                        field.onChange(value);
                                        setProduct((prevState) => ({ ...prevState, brand: value ?? null }));
                                    }}
                                />
                            )}
                        />
                    )}
                </Box>
            )}

            {!isOwner && (
                <Controller
                    name="ownedBy"
                    control={control}
                    render={({ field }) => (
                        <OwnedByField
                            value={field.value ?? null}
                            appearance="product-v2"
                            label={t("product.v2.organization.structure_owner", "Owner")}
                            onChange={(value) => {
                                setProduct((prevState) => ({ ...prevState, ownedBy: value }));
                            }}
                        />
                    )}
                />
            )}

            {canShowAssociatedStructure && (
                <AssociatedStructuresFieldV2
                    ownedBy={owningCompany}
                    value={product.shares}
                    onChange={(value) => {
                        setProduct((prevState) => ({ ...prevState, shares: value }));
                    }}
                />
            )}

            {hasProductTagTypes && hasContentBeforeTags && (
                <Box
                    role="separator"
                    aria-orientation="horizontal"
                    sx={{ borderBottomWidth: 0, height: 0, position: "relative", width: "100%" }}
                >
                    <Box
                        aria-hidden
                        sx={{
                            borderBottomColor: theme.custom.strokes.containerDivider.color,
                            borderBottomStyle: theme.custom.strokes.containerDivider.style,
                            borderBottomWidth: theme.custom.strokes.containerDivider.width,
                            insetInline: 0,
                            position: "absolute",
                            top: 0,
                        }}
                    />
                </Box>
            )}

            {hasProductTagTypes && (
                <Stack sx={{ gap: theme.spacing(1) }}>
                    <Typography variant="body-bold">{t("entity.tags.title", "Tags")}</Typography>
                    <ProductTagFieldV2 product={product} setProduct={setProduct} />
                </Stack>
            )}
        </Stack>
    );
};
